import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ItemIncompleto {
  item: string;
  erro: string;
}

export interface ValidacaoPedido {
  valido: boolean;
  itensIncompletos: ItemIncompleto[];
  totalItens: number;
  itensCompletos: number;
}

export function useValidacaoPedido() {
  const [validando, setValidando] = useState(false);
  const { toast } = useToast();

  const validarPedido = async (pedidoId: string | string[]): Promise<ValidacaoPedido | null> => {
    setValidando(true);
    
    // Normalizar input para array
    const pedidoIds = Array.isArray(pedidoId) ? pedidoId : [pedidoId];
    
      console.log('🔍 [useValidacaoPedido] Iniciando validação:', { 
      pedidoIds,
      quantidade: pedidoIds.length,
      timestamp: new Date().toISOString()
    });

    console.log('📋 [VALIDAÇÃO PREVENTIVA] Verificando campos obrigatórios vazios antes da RPC...');

    try {
      // Buscar números formatados dos pedidos
      const { data: pedidosData } = await supabase
        .from('pedidos')
        .select('id, numero_pedido')
        .in('id', pedidoIds);

      const pedidoNumeroMap = new Map(
        pedidosData?.map(p => [p.id, p.numero_pedido]) || []
      );

      // ✅ Buscar total REAL de itens antes da validação
      const { count: totalItensReais } = await supabase
        .from('itens_pedido')
        .select('*', { count: 'exact', head: true })
        .in('pedido_id', pedidoIds);

      console.log(`📊 [useValidacaoPedido] Total de itens nos pedidos: ${totalItensReais || 0}`);

      // ✅ Validação de obrigatoriedades agora é feita via RPC que respeita as configurações do grupo
      // Removida verificação hardcoded de tipo_chip - agora respeita grupo_obrigatoriedades_config

      // Validar cada pedido e agregar resultados
      const resultadosValidacao = await Promise.all(
        pedidoIds.map(async id => {
          console.log(`🔍 [useValidacaoPedido] Validando pedido individual: ${id.substring(0, 8)}...`);
          const resultado = await supabase.rpc('validate_pedido_itens', {
            p_pedido_id: id,
            p_destino: 'PEDIDO_FECHADO'
          });
          console.log(`📋 [useValidacaoPedido] Resultado para ${id.substring(0, 8)}:`, {
            hasError: !!resultado.error,
            error: resultado.error,
            data: resultado.data
          });
          return resultado;
        })
      );

      // Processar resultados - NÃO lançar exceção, agregar todos os erros
      const todosErros: ItemIncompleto[] = [];
      let algumInvalido = false;

      // Usar for...of para permitir await
      for (let index = 0; index < resultadosValidacao.length; index++) {
        let resposta = resultadosValidacao[index];
        const pedidoId = pedidoIds[index];
        
        // 🔧 CORREÇÃO 2: Auto-corrigir erro de ENUM vazio antes de processar
        if (resposta.error?.code === '22P02' && resposta.error?.message?.includes('operadora_doadora_enum')) {
          console.log('🔧 [useValidacaoPedido] Detectado erro de ENUM vazio, corrigindo e retentando...');
          
          // Tentar validação novamente (a RPC já corrige automaticamente)
          const retry = await supabase.rpc('validate_pedido_itens', {
            p_pedido_id: pedidoId,
            p_destino: 'PEDIDO_FECHADO'
          });
          
          console.log('✅ [useValidacaoPedido] Retry após correção:', {
            hasError: !!retry.error,
            data: retry.data
          });
          
          // Se retry funcionou, substituir resposta original
          if (!retry.error) {
            resposta = retry;
          }
        }
        
        // Se RPC retornou erro técnico, fazer validação item-a-item
        if (resposta.error) {
          console.error(`❌ [useValidacaoPedido] Erro na RPC para ${pedidoId.substring(0, 8)}:`, resposta.error);
          
          const pedidoNumero = pedidoNumeroMap.get(pedidoId) || 'Pedido';
          
          // Buscar itens do pedido e validar individualmente
          const { data: itensPedido } = await supabase
            .from('itens_pedido')
            .select('id')
            .eq('pedido_id', pedidoId)
            .order('created_at') as { data: Array<{ id: string }> | null };
          
          if (itensPedido && itensPedido.length > 0) {
            console.log(`🔍 [useValidacaoPedido] Validando ${itensPedido.length} itens individualmente para pedido ${pedidoNumero}`);
            
            // ✅ BATCH: validar todos os itens em paralelo (em vez de sequencial)
            const validacaoResults = await Promise.all(
              itensPedido.map(item => 
                supabase.rpc('validate_item_campos_obrigatorios', { 
                  p_item_id: item.id, 
                  p_contexto: 'PEDIDO_FECHADO' 
                })
              )
            );

            validacaoResults.forEach((result, i) => {
              const validacaoItem = result.data;
              if (validacaoItem) {
                const errosItem = Array.isArray(validacaoItem) ? validacaoItem : [];
                
                if (errosItem.length > 0) {
                  const camposFaltando = errosItem.map((e: any) => e.campo || e.nome).filter(Boolean);
                  
                  todosErros.push({
                    item: `${pedidoNumero} - Item ${i + 1}`,
                    erro: `Campos obrigatórios faltando: ${camposFaltando.join(', ')}`
                  });
                }
              }
            });
            
            algumInvalido = true;
          } else {
            // Fallback se não conseguir buscar itens
            todosErros.push({
              item: `${pedidoNumeroMap.get(pedidoId) || 'Pedido'} (erro técnico)`,
              erro: 'Erro ao validar itens. Verifique os campos obrigatórios e tente novamente.'
            });
            algumInvalido = true;
          }
          
          continue; // Continua para o próximo pedido
        }

        // Processar resposta normal
        const resultado: any = Array.isArray(resposta.data) 
          ? resposta.data[0] 
          : resposta.data;
        
        if (!resultado) {
          console.error(`❌ [useValidacaoPedido] Resultado null para pedido ${pedidoId}`);
          algumInvalido = true;
          todosErros.push({
            item: `${pedidoNumeroMap.get(pedidoId) || 'Pedido'} (dados inválidos)`,
            erro: 'Resposta vazia da validação'
          });
          continue;
        }

        // ✅ CORREÇÃO: Backend retorna "ok", mapear para "valido"
        const isValido = resultado.ok ?? resultado.valido ?? true;
        
        if (!isValido) {
          algumInvalido = true;
        }

        const erros = resultado.errors || [];
        if (Array.isArray(erros)) {
          erros.forEach((itemErro: any) => {
            const produtoNome = itemErro.produto_nome || 'Produto';
            const itemErrors = itemErro.errors || [];
            
            if (Array.isArray(itemErrors) && itemErrors.length > 0) {
              const camposFaltando = itemErrors.map((e: any) => e.nome || e.campo).filter(Boolean);
              todosErros.push({
                item: `${produtoNome} (${pedidoNumeroMap.get(pedidoId) || 'Pedido'})`,
                erro: `Campos faltando: ${camposFaltando.join(', ')}`
              });
            }
          });
        }
      }

      console.log('✅ [useValidacaoPedido] Validação agregada concluída:', {
        pedidosValidados: pedidoIds.length,
        valido: !algumInvalido,
        errosEncontrados: todosErros.length,
        detalhesErros: todosErros
      });

      if (algumInvalido && todosErros.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Itens incompletos',
          description: `${todosErros.length} campo(s) obrigatório(s) faltando em ${pedidoIds.length} pedido(s)`,
        });
      }

      // Contar quantos ITENS ÚNICOS têm erros (não quantos erros existem)
      const itensUnicosComErro = new Set(
        todosErros.map(erro => erro.item)
      ).size;

      console.log('📊 [useValidacaoPedido] Cálculo final:', {
        totalItens: totalItensReais,
        errosTotais: todosErros.length,
        itensUnicosComErro: itensUnicosComErro,
        itensCompletos: Math.max(0, (totalItensReais || 0) - itensUnicosComErro)
      });

      return {
        valido: !algumInvalido,
        itensIncompletos: todosErros,
        totalItens: totalItensReais || 0,
        itensCompletos: Math.max(0, (totalItensReais || 0) - itensUnicosComErro), // ✅ Corrigido: conta itens únicos
      };
    } catch (error: any) {
      console.error('💥 [useValidacaoPedido] Erro fatal:', {
        error,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code
      });
      toast({
        variant: 'destructive',
        title: 'Erro ao validar pedido',
        description: error.message || 'Erro desconhecido ao validar itens',
      });
      return null;
    } finally {
      setValidando(false);
      console.log('🏁 [useValidacaoPedido] Validação finalizada');
    }
  };

  return { validarPedido, validando };
}
